import React, { useMemo, useRef, useState } from "react";

import { Box, IconButton, Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  ColumnFilter,
  GeneralActions,
  SearchSuggest,
} from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import Button, { Color } from "Components/Form/Button";
import IconCloseCircle from "Components/Generic/Icons/CloseCircle";
import { Add as IconAdd, Eye as IconView } from "iconsax-react";
import { SearchAttribute } from "Models/search.attribute";
import { Filters } from "Models/shipeezi.table";
import { Team as ITeam } from "Models/team";
import { useTranslation } from "react-i18next";
import { Status } from "Util/enums";
import { filter } from "Util/filter";
import { filterSearch } from "Util/filterSearch";
import { onSearch } from "Util/onSearch";
import { paginate } from "Util/paginate";

import { getTeamTableColumns } from "../../../../../../People/Members/Team/getTeamTableColumns";
import { useEquipmentContext } from "../../../context/Equipment";
import { EmptyState } from "../EmptyState";
import { AddTeamModal } from "./AddTeamModal";
import useStyles from "./styles";
import ViewTeamModal, { HandleViewModalProps } from "./ViewTeamModal";

export const Team: React.FC = () => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { equipment, onUpdateEquipment, setEquipment } = useEquipmentContext();
  const [isAddTeamModalOpened, setIsAddTeamModalOpened] = useState(false);

  const [pageSize, setPageSize] = useState<number>(20);
  const [page, setPage] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();

  const [searchAttributes, setSearchAttributes] = useState<SearchAttribute[]>([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchOptions, setSearchOptions] = useState<ColumnFilter[]>([]);

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const viewModalRef = useRef<HandleViewModalProps>(null);

  const teamsAdded = useMemo(() => {
    return equipment?.teams || [];
  }, [equipment]);

  const teamsRenderable = useMemo(() => {
    let data = teamsAdded || [];

    if (searchAttributes.length > 0) data = filterSearch(data, searchAttributes);

    if (filters) data = filter(data, filters);

    return searchAttributes.length > 0 ? data : paginate(data, page > 0 ? page - 1 : 0, pageSize);
  }, [teamsAdded, searchAttributes, page, pageSize, filters]);

  const columns = getTeamTableColumns({ translate });

  const onChangeSearchOptions = (options: ColumnFilter[]) => {
    setSearchOptions(options);
    const attributes = onSearch(options);

    setSearchAttributes(attributes);
  };

  const renderSearchSuggest = () => {
    return (
      <SearchSuggest
        columns={[{ name: "name", title: translate("name") }]}
        columnsSelected={searchOptions}
        onChangeColumnsSelected={onChangeSearchOptions}
        inputValue={searchInput}
        onChangeInput={setSearchInput}
        placeholder={translate("search")}
      />
    );
  };

  const handleFilter = (value: Filters) => {
    const filtersKeys = Object.getOwnPropertyNames(value);

    setFilters(filtersKeys && filtersKeys?.length ? value : undefined);
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["searchSuggest", "handleFilterBy"],
      right: ["newRecord"],
    },
    searchSuggest: renderSearchSuggest(),
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "isActive",
          options: [
            { label: Status.ACTIVE, value: "1" },
            { label: Status.INACTIVE, value: "0" },
          ],
        },
      ],
      onChange: handleFilter,
    },
    newRecord: (
      <Button
        startIcon={<IconAdd size={20} />}
        customColor={Color.SECONDARY_DARK}
        onClick={() => setIsAddTeamModalOpened(true)}
      >
        {translate("addTeam")}
      </Button>
    ),
  };

  const onAddTeamsSelected = (teams: any[]) => {
    const map: Map<number, any> = new Map();

    teams.forEach((team) => {
      map.set(team.id, team);
    });

    onUpdateEquipment("teams", Array.from(map.values()));
  };

  const onRemoveTeamSelected = (teamId: number) => {
    setEquipment((prev) => ({
      ...(prev || {}),
      teams: (prev?.teams || []).filter((team) => Number(team.id) !== Number(teamId)),
    }));
  };

  return (
    <Box className={classes.main}>
      {teamsAdded?.length ? (
        <ShipeeziNewGrid
          data={teamsRenderable}
          totalItems={teamsAdded.length}
          columns={columns}
          showSelection={false}
          showSort={false}
          pageSize={pageSize}
          currentPage={page}
          loadingData={false}
          pageSizeList={[20, 20, 40, 60]}
          onChangePageSize={setPageSize}
          onNextPage={setCurrentPage}
          onPreviousPage={setCurrentPage}
          onGoToPage={setCurrentPage}
          positionActionsColumn="right"
          keyExtractor={(data) => data?.id?.toString()}
          emptyData={translate("noInformationToBeShown")}
          generalActions={generalActions}
          rowActionsExtra={[
            {
              title: translate("view"),
              key: "view",
              buttonAndAction: (_, team: ITeam) => (
                <Tooltip title={translate("view")} placement="top">
                  <IconButton
                    className={classes.iconButton}
                    onClick={() => viewModalRef.current?.handleOpenModal(team)}
                  >
                    <IconView size={20} color={colors.gray600} />
                  </IconButton>
                </Tooltip>
              ),
            },
            {
              title: translate("remove"),
              key: "remove",
              buttonAndAction: (teamId) => (
                <Tooltip title={translate("remove")} placement="top">
                  <IconButton
                    className={classes.iconButton}
                    onClick={() => onRemoveTeamSelected(teamId)}
                  >
                    <IconCloseCircle width={20} height={20} color={colors.error500} />
                  </IconButton>
                </Tooltip>
              ),
            },
          ]}
        />
      ) : (
        <EmptyState
          descriptionResource={translate("teams").toLowerCase()}
          buttonResource={translate("addTeam")}
          onAddNew={() => setIsAddTeamModalOpened(true)}
        />
      )}
      <AddTeamModal
        isOpened={isAddTeamModalOpened}
        onClose={() => setIsAddTeamModalOpened(false)}
        teamsIdAdded={teamsAdded.map((team) => Number(team.id))}
        onConfirmAddTeams={onAddTeamsSelected}
      />
      <ViewTeamModal ref={viewModalRef} />
    </Box>
  );
};
